import { SetStateAction, useContext, useState } from "react";
import { LangContext, LangContextType, useAuth } from "../../../utilities/customHooks";
import { useSuspenseQuery } from "@tanstack/react-query";
import { queryKeys } from "../../../utilities/queryKeys";
import Button from "../../../Components/Button";
import { useNavigate } from "react-router-dom";
import textData from "../../../textData.json";
import { getSPFirms, getLatestMembershipApplication } from "../../../services/founderService";
import { useSendSpfConfirm, useSendSpfRequest } from "../../../services/founderMutations";
import * as T from "../../../utilities/frontendTypes";
import { inputClasses } from "../../../Components/Form/InputField";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../Components/ui/select";
import RoadmapCard from "../../../Components/RoadmapCard"
import DashboardPageLayout, { RoadmapSection } from "../../../Components/DashboardPageLayout"
import { MapPinIcon } from "../../../Components/icons/DashboardIcons"
import EducationSection from "../../../Components/EducationSection"
import ProgramDetailsFooter from "../../../Components/ProgramDetailsFooter"

function ExpertMetDashboard(
  {
    companyNameForDashboard,
  }
  :
  {
    companyNameForDashboard: string,
  }
) {
  const navigate = useNavigate();
  const spfConfirmMutation = useSendSpfConfirm();
  const spfRequestMutation = useSendSpfRequest();
  const { langState } = useContext(LangContext) as LangContextType
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [requestNewSpf, setRequestingNewSpf] = useState(false);
  const [spf, setSpf] = useState("");
  const [newSpfDetails, setNewSpfDetails] = useState("");
  const {userRegion} = useAuth();

  const { data: firmsData } = useSuspenseQuery({
    queryKey: queryKeys.providers.firms(),
    queryFn: () => getSPFirms(),
  });
  const { data: applicationData } = useSuspenseQuery({
    queryKey: queryKeys.members.latestApplication(),
    queryFn: () => getLatestMembershipApplication(),
  });

  if (userRegion === null) return null;

  const regionalFirmHeaderData: T.SPFirmHeader[] = firmsData.data;
  const application = applicationData.data;
  const assignedSpId = application.spId || application.preferredSpId || "";
  const assignedSp = regionalFirmHeaderData.find(firm => firm.firmId === assignedSpId);

  const regionalFirmHeaders = regionalFirmHeaderData;

  const handleSpfChange = (value: string) => {
    setSpf(value)
  }

  const handleNewSpfDetailsChange = (event: { target: { value: SetStateAction<string>; }; }) => {
    setNewSpfDetails(event.target.value);
  };

  const requestSpf = () => {
    spfRequestMutation.mutate(newSpfDetails, {
      onSuccess: () => {
        setIsModalOpen(false);
        navigate("/dashboard");
      },
    });
  }

  const submitServiceProvider = () => {
    const spfToConfirm = spf || assignedSpId;
    spfConfirmMutation.mutate(spfToConfirm, {
      onSuccess: () => setIsModalOpen(false),
    });
  }

  return (
    <DashboardPageLayout>
      {isModalOpen &&
        <div className='largeModal'>
          <div className='largeModalInner !h-fit'>
            <div className='flex justify-end w-full'>
              <button onClick={() => setIsModalOpen(false)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M9 1L1 9M1 1L9 9" stroke="#1A2E25" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
            </div>
            {requestNewSpf ?
              <div className="flex flex-col gap-4">
                <p className="font-semibold bodyLarger">Please share the contact details of the individual you are working with so that we can start a partnership with them and they can continue to work with you during your ElevateIP project. We will reach out to them and once that partnership is formalized we will contact you to initiate the Statement of Work</p>
                <div className="flex flex-col w-full gap-2">
                  <textarea className={`min-h-[112px] + ${inputClasses}`} defaultValue={newSpfDetails} placeholder="Provide the contact details for your service provider..." onChange={handleNewSpfDetailsChange}></textarea>
                </div>
                <Button
                  buttonText="Submit"
                  disabled={newSpfDetails === ""}
                  actionHandler={() => requestSpf()}
                />
              </div>
            : assignedSp ?
              <div className="flex flex-col gap-4">
                <div className="w-full flex flex-col gap-4">
                  <p className="font-semibold bodyLarger">Please confirm your assigned Service Provider:</p>
                  <div className="bg-gray-100 p-4 rounded">
                    <p className="text-lg font-semibold">{assignedSp.name}</p>
                    {assignedSp.url && <p className="text-sm text-gray-600">{assignedSp.url}</p>}
                  </div>
                  <p className="text-sm">If you would like to work with a different provider, you may request a change below.</p>
                </div>
                <Button
                  buttonText="Confirm Service Provider"
                  actionHandler={() => submitServiceProvider()}
                  buttonType="primary"
                />
                <Button
                  buttonText="Request A Different Provider"
                  actionHandler={() => setRequestingNewSpf(true)}
                  buttonType="secondary"
                />
              </div>
            :
              <div className="flex flex-col gap-4">
                <div className="w-full flex flex-col">
                  <p className="font-semibold bodyLarger">Please select a Service Provider from the list of providers working with ElevateIP.</p>
                  <p>If you would like to work with a service provider not on this list, Communitech will need to onboard them first, which may delay your project. However, ultimately, you may work with a service provider of your choice.</p>
                </div>
                <Select value={spf || undefined} onValueChange={handleSpfChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Please make a selection" />
                  </SelectTrigger>
                  <SelectContent>
                    {regionalFirmHeaders
                      .slice() // create a shallow copy to avoid mutating the original array
                      .sort((a, b) => a.name.localeCompare(b.name))
                      .map((firm) => (
                        <SelectItem key={firm.firmId} value={firm.firmId}>
                          {firm.name}
                        </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  buttonText="Select Service Provider"
                  disabled={spf === ""}
                  actionHandler={() => submitServiceProvider()}
                />
                <Button
                  buttonText="Request A Different Provider"
                  actionHandler={() => setRequestingNewSpf(true)}
                />
              </div>
            }
          </div>
        </div>
      }
      <h4>{textData.Pages.Dashboards.FounderDashboards.selectSpfDashboard.Portal[langState]}</h4>
      <div className="flex justify-between items-start self-stretch w-full">
        <RoadmapSection title={textData.Pages.Dashboards.FounderDashboards.ExpertMetDashboard.Roadmap[langState]}>
            <RoadmapCard status="complete" title={textData.Pages.Dashboards.FounderDashboards.ExpertMetDashboard.Roadmap2[langState]} />
            <RoadmapCard status="complete" title={textData.Pages.Dashboards.FounderDashboards.ExpertMetDashboard.Roadmap3[langState]} />
            <RoadmapCard status="current" title={textData.Pages.Dashboards.FounderDashboards.selectSpfDashboard.Roadmap3[langState]} subtitle={textData.Pages.Dashboards.FounderDashboards.selectSpfDashboard.Roadmap3Sub[langState]} />
            <RoadmapCard status="locked" title={textData.Pages.Dashboards.FounderDashboards.selectSpfDashboard.Roadmap4[langState]} />
          </RoadmapSection>
        <div className="oppositeRoadMap">
          <div className="nextStepCont">
            <div className="flex gap-2 nextStepFull">
              <div className="flex-col gap-2">
                <h3 className="font-semibold">{textData.Pages.Dashboards.FounderDashboards.selectSpfDashboard.NextStepTitle[langState]}</h3>
                <h4 className="">{textData.Pages.Dashboards.FounderDashboards.selectSpfDashboard.NextStep[langState]}</h4>
                <h4 className="">{textData.Pages.Dashboards.FounderDashboards.selectSpfDashboard.NextStep2[langState]}</h4>
              </div>
              <MapPinIcon />
            </div>
            <Button
              buttonText={textData.Pages.Dashboards.FounderDashboards.selectSpfDashboard.NextStepButton[langState]}
              buttonType='primaryFull'
              actionHandler={() => {
                setIsModalOpen(true);
              }}
            />
          </div>
          <EducationSection textPrefix="ExpertMetDashboard" bulletTextPrefix="SubmittedFounderDashboard" buttonTextPrefix="UnsubmittedFounderDashboard" />
        </div>
      </div>
      <ProgramDetailsFooter textPrefix="ExpertMetDashboard" />
    </DashboardPageLayout>
  )
}

export default ExpertMetDashboard;
